import type { JSX } from "react";

import { componentManager, rwc } from "@frontend/bridge/ComponentManager";
import type { IconName } from "@frontend/components/atoms/Icon";
import { useIslandHost } from "@frontend/contexts";

import { Tree } from "./Tree";
import {
  type TreeDataProps,
  type TreeNodeData,
  treeDataSchema,
} from "./Tree.schema";
import type { TreeNode } from "./Tree.types";
import { TREE_ITEM_TAG_NAME, TREE_TAG_NAME } from "./constants";

declare module "@frontend/bridge/types" {
  interface ComponentRegistryMap {
    [TREE_TAG_NAME]: { props: TreeDataProps; handle: undefined };
  }
  interface InertTagNames {
    [TREE_ITEM_TAG_NAME]: true;
  }
}

const RWCTreeItem = rwc(TREE_ITEM_TAG_NAME);

function getDirectText(el: Element): string {
  return Array.from(el.childNodes)
    .filter((node) => node.nodeType === Node.TEXT_NODE)
    .map((node) => node.textContent ?? "")
    .join("")
    .trim();
}

function parseTreeItemElement(el: HTMLElement): TreeNodeData {
  const children = Array.from(
    el.querySelectorAll<HTMLElement>(`:scope > ${RWCTreeItem.tagName}`),
  ).map(parseTreeItemElement);

  return {
    label: el.getAttribute("label") ?? getDirectText(el),
    icon: el.getAttribute("icon") ?? undefined,
    type: (el.getAttribute("type") as TreeNodeData["type"]) ?? undefined,
    defaultExpanded: el.hasAttribute("expanded")
      ? el.getAttribute("expanded") !== "false"
      : undefined,
    disabled: el.hasAttribute("disabled"),
    children: children.length > 0 ? children : undefined,
  };
}

export function readTreeSlotted(el: HTMLElement): Partial<TreeDataProps> {
  const items = Array.from(
    el.querySelectorAll<HTMLElement>(`:scope > ${RWCTreeItem.tagName}`),
  ).map(parseTreeItemElement);

  return { items };
}

function toTreeNode(data: TreeNodeData): TreeNode {
  return {
    ...data,
    icon: data.icon as IconName | undefined,
    children: data.children?.map(toTreeNode),
  };
}

function RWCTreeComponent({ items = [] }: TreeDataProps): JSX.Element {
  const host = useIslandHost();

  function handleSelect(node: TreeNode) {
    host?.dispatchEvent(
      new CustomEvent("tree:select", { detail: { node }, bubbles: true }),
    );
  }

  return <Tree items={items.map(toTreeNode)} onSelect={handleSelect} />;
}

componentManager.register(
  RWCTreeItem,
  rwc(TREE_TAG_NAME, {
    component: RWCTreeComponent,
    dataSchema: treeDataSchema,
    usesSlots: true,
    readSlotted: readTreeSlotted,
  }),
);
